import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Briefcase, CheckSquare, AlertTriangle, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Stats {
  activeJobs: number;
  planningJobs: number;
  blockedJobs: number;
  tasksDueToday: number;
  overdueTasks: number;
  openTasks: number;
}

export function HomeDashboardStats() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const today = new Date().toISOString().split("T")[0];

        const [jobsRes, tasksRes] = await Promise.all([
          supabase.from("jobs").select("status"),
          supabase
            .from("tasks")
            .select("id, due_date, status, deleted_at")
            .is("deleted_at", null)
            .neq("status", "Done"),
        ]);

        const jobs = jobsRes.data || [];
        const tasks = tasksRes.data || [];

        const activeJobs = jobs.filter((j) => j.status === "In Progress").length;
        const planningJobs = jobs.filter((j) => j.status === "Planning").length;
        const blockedJobs = jobs.filter(
          (j) => j.status === "Blocked" || j.status === "On Hold"
        ).length;

        const tasksDueToday = tasks.filter((t) => t.due_date === today).length;
        const overdueTasks = tasks.filter(
          (t) => t.due_date && t.due_date < today
        ).length;
        const openTasks = tasks.length;

        setStats({ activeJobs, planningJobs, blockedJobs, tasksDueToday, overdueTasks, openTasks });
      } catch (err) {
        console.error("Dashboard stats error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  const cards = [
    {
      label: "Active Jobs",
      value: stats.activeJobs,
      sub: `${stats.planningJobs} planning`,
      icon: Briefcase,
      color: "text-primary",
      bgColor: "bg-primary/10",
      onClick: () => navigate("/jobs"),
    },
    {
      label: "Open Tasks",
      value: stats.openTasks,
      sub: `${stats.tasksDueToday} due today`,
      icon: CheckSquare,
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-500/10",
      onClick: () => navigate("/today-board"),
    },
    {
      label: "Overdue",
      value: stats.overdueTasks,
      sub: "tasks past due",
      icon: AlertTriangle,
      color: stats.overdueTasks > 0 ? "text-destructive" : "text-muted-foreground",
      bgColor: stats.overdueTasks > 0 ? "bg-destructive/10" : "bg-muted/50",
      onClick: () => navigate("/today-board"),
    },
    {
      label: "Blocked/Hold",
      value: stats.blockedJobs,
      sub: "jobs need attention",
      icon: Clock,
      color: stats.blockedJobs > 0 ? "text-orange-600 dark:text-orange-400" : "text-muted-foreground",
      bgColor: stats.blockedJobs > 0 ? "bg-orange-500/10" : "bg-muted/50",
      onClick: () => navigate("/jobs"),
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Card
            key={card.label}
            className="cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 border-border bg-card/80 backdrop-blur-sm"
            onClick={card.onClick}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`rounded-md p-1.5 ${card.bgColor}`}>
                  <Icon className={`h-4 w-4 ${card.color}`} />
                </div>
                <span className="text-xs font-medium text-muted-foreground">
                  {card.label}
                </span>
              </div>
              <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{card.sub}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
